import { IBookingOptionsUpsellState } from 'booking-creator/reducers/bookingOptionsUpsell/types';
import {
  IBooking,
  IBookingMenuOption,
  IBookingMenuOptionExtrasUpdater,
  IBookingOptionPayment,
  IInfoAtTime,
  IMenuOptionsResponse,
  IPaymentDetailsBase,
  IPouchServicePaymentOption,
  IServicePaymentOption,
  ISimpleBookingOption,
  IUpdateActiveChildMenuOptionPayload,
  IUpdateCachedChildMenuOptionDetails,
  PricingTypes,
  servicePaymentType,
} from 'shared-types/SharedTypes';
import {
  IActiveChildMenuOption,
  IParentMenuOption,
} from 'external-components/booking-options-section/ChildMenuOptionOverlay/types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { cloneDeep, isEmpty } from 'lodash-es';
import { DiaryRootState } from 'appRoot/rootReducer';
import { displayActions } from 'booking-creator/reducers/display/slice';
import { IDiaryService } from 'shared-types/DiaryTypes';
import {
  getActivePaymentDetail,
  updatePaymentBasedOnBookingOptions,
} from 'booking-creator/reducers/payment/slice';
import BookingOptionsManagerService from 'external-services/booking-options-manager-service/bookingOptionsManager.service';
import BookingValidationService from 'external-services/booking-validation-service';
import BookingUpsellOptionsService from 'external-services/booking-upsell-options-service/bookingUpsellOptions.service';
import PaymentService from 'external-services/diary-payment-service';
import { first, map } from 'rxjs/operators';
import AdminService from 'external-services/diary-admin-service/admin.service';
import { BookingMode } from 'booking-creator/reducers/display/types';
import BookingService from 'external-services/booking-service';
import { errorStateActions, getAccordionDetail } from 'app/reducers/errorState/slice';
import { CONTACT_MSG, PROBLEMS_MSG, REFRESH_MSG } from 'appRoot/app-error-messages';
import {MenuOptionsService} from "external-services/menu-options-service";

const NS = 'BookingOptionsUpsellSlice';

const initialState: IBookingOptionsUpsellState = {
  activeChildMenuOption: {} as IActiveChildMenuOption,
  cachedMenuOptionDetails: [] as ISimpleBookingOption[],
  editCachedMenuOptionDetails: [{ id: '' }],
  isBookingOption: false,
  bookingOptionsValid: false,
  isUpsellOptions: false,
  upsellOptionsValid: false,
  showBookingOptions: true,
  showUpsellOptions: true,
  temporaryPaymentDetailsForBO: undefined,
  bookingUpsellOptionDate: new Date(),
  allBookingOptions: [],
  enableBookingUpsellOptions: true,
  enableBookingOptions: true,
  isPaxThresholdValidForEdit: true,
};

export const settingUpDetailsForBookingUpsellOptions = createAsyncThunk(
  'BookingOptionsUpsell/settingUpDetailsForBookingUpsellOptions',
  async (
    payload: { service: IDiaryService; people: number; bookingDate: Date; mode: BookingMode },
    { dispatch, getState }
  ): Promise<{
    editCachedMenuOptionDetails: { id: string }[];
    isBookingOption: boolean;
    bookingOptionsValid: boolean;
    isUpsellOptions: boolean;
    upsellOptionsValid: boolean;
    newPaymentDetails: IPaymentDetailsBase;
    isPaxThresholdValidForEdit: boolean;
  } | null> => {
    const { displayReducer, bookingOptionsUpsellReducer } = getState() as DiaryRootState;
    const booking = displayReducer.activeBooking as IBooking;

    const existingBookingOptions = bookingOptionsUpsellReducer.allBookingOptions;
    let allBookingOptions: IPouchServicePaymentOption[] = [];
    const activePaymentDetails = getActivePaymentDetail(
      payload.service,
      payload.people,
      payload.bookingDate
    );
    if (activePaymentDetails) {
      // We have to check for pax validation for BO but not for Upsell options
      if (
        !isEmpty(activePaymentDetails.menuOptions) ||
        !isEmpty(activePaymentDetails.menuOptionsUpsell)
      ) {
        const isPaxThresholdValid = activePaymentDetails?.peopleRequired <= payload.people;
        if (isEmpty(existingBookingOptions)) {
          const BOFromAPI = await BookingOptionsManagerService.getAll();
          allBookingOptions = cloneDeep(BOFromAPI);
          dispatch(actions.setAllBookingOptionsFromAPI(BOFromAPI));
        } else {
          allBookingOptions = cloneDeep(existingBookingOptions);
        }
        const menuOptions = (activePaymentDetails.menuOptions || []).map(
          (id) =>
            Object.assign(
              allBookingOptions?.find((option) => option._id === id),
              { id: id }
            ) // adding a object id in MenuOption to has the same type like ISimpleBookingOption
        );

        const menuOptionsUpsell = (activePaymentDetails.menuOptionsUpsell || []).map(
          (id) =>
            Object.assign(
              allBookingOptions?.find((option) => option._id === id),
              { id: id }
            ) // adding a object id in MenuOption to has the same type like ISimpleBookingOption
        );

        const { isUpsellOptions, upsellOptionsValid } =
          BookingValidationService.setUpsellOptionsValidity(activePaymentDetails);
        let bookingOptionsValid = false;
        const { isBookingOption, bookingOptionsValid: _bookingOptionsValid } =
          BookingValidationService.setBookingOptionsValidity(activePaymentDetails, payload.people);
        bookingOptionsValid = _bookingOptionsValid;

        const newOptions = {
          options: (menuOptions as ISimpleBookingOption[]) || [],
          optionsUpsell: (menuOptionsUpsell as ISimpleBookingOption[]) || [],
        };
        const newPaymentDetails = { ...activePaymentDetails, ...newOptions };
        if (payload.mode === BookingMode.newBooking) {
          dispatch(displayActions.resetSelectedAllBookingOptions());
        } else if (payload.mode === BookingMode.editBooking) {
          const selectedMenuOptions = booking.selectedOptions
            ? BookingService.convertSelectedBookingOptions(booking.selectedOptions)
            : [];
          dispatch(displayActions.setSelectedMenuOptions(selectedMenuOptions));


          //--start---follow code is for make it valid when initial edit booking
          // if change pax, it will set to false in useEffect in BookingUpsellOptionsPanel/index.tsx
          // the reason put it here is for the order of update state is messy, so we need to set it here
          // or it will be overwritten again by this function
          const pricingType: string = activePaymentDetails.pricingType;
          const singleMenuPerBooking: boolean = activePaymentDetails.singleMenuPerBooking;
          const isCheckbox = pricingType === PricingTypes.PerBooking && !singleMenuPerBooking;
          if (
            // radio or checkbox for BO selections
            ((pricingType === PricingTypes.PerPax && singleMenuPerBooking) || isCheckbox) &&
            booking.selectedOptions?.length > 0
          ) {
            bookingOptionsValid = true;
          }

          if (!singleMenuPerBooking && pricingType === PricingTypes.PerPax) {
            const combinedCovers = MenuOptionsService.getCombinedCovers(newPaymentDetails.options, selectedMenuOptions);
            const remaining = MenuOptionsService.getRemainingCovers(booking.people, combinedCovers);
            bookingOptionsValid = remaining === 0;
          }
          if (booking.selectedOptions?.length === 0) {
            bookingOptionsValid = true;
          }
          //--end---
        }
        return {
          editCachedMenuOptionDetails: updateMenuOptionCachedDetails(allBookingOptions),

          // if we're not showing booking options, then they should be considered valid
          bookingOptionsValid: bookingOptionsUpsellReducer.showBookingOptions
            ? bookingOptionsValid
            : true,

          // upsell should always be valid, since they are optional
          upsellOptionsValid: true,
          isUpsellOptions,
          isBookingOption,
          newPaymentDetails,
          isPaxThresholdValidForEdit: isPaxThresholdValid,
        };
      }
    }
    return null;
  }
);

export const updateActiveChildBookingUpsellOption = createAsyncThunk(
  'BookingOptionsUpsell/updateActiveChildBookingUpsellOption',
  async (
    payload: { parentMenuOption: IParentMenuOption },
    { dispatch, getState }
  ): Promise<void> => {
    return new Promise(async (resolve) => {
      const { bookingOptionsUpsellReducer } = getState() as DiaryRootState;

      const cachedMenuOptionDetails = bookingOptionsUpsellReducer.cachedMenuOptionDetails;

      const parentMenuOption = payload.parentMenuOption;
      const implicitIds =
        parentMenuOption && parentMenuOption.childMenuOptionIds
          ? parentMenuOption.childMenuOptionIds
          : [];
      const explicitIds =
        parentMenuOption && parentMenuOption.explicitChildMenuOptionIds
          ? parentMenuOption.explicitChildMenuOptionIds
          : [];

      if (!implicitIds.length && !explicitIds.length) {
        const payload: any = null;
        await dispatch(updateActiveChildMenuOption(payload));
        resolve(null);
        return;
      }

      const ids: string[] = implicitIds.concat(explicitIds);

      // if all details are already cached there is no need to get them again, so we bypass the request and use the cache
      const allAreCached: boolean = cachedMenuOptionDetails
        ? ids.every((id) => cachedMenuOptionDetails.find((o) => o.id === id))
        : false;
      if (allAreCached) {
        const payload: any = {};
        payload.implicitChildMenuOptions = implicitIds.map((id) =>
          cachedMenuOptionDetails.find((o) => o.id === id)
        );
        payload.explicitChildMenuOptions = explicitIds.map((id) =>
          cachedMenuOptionDetails.find((o) => o.id === id)
        );
        await dispatch(updateActiveChildMenuOption(payload));
        resolve(null);
        return;
      }

      try {
        BookingUpsellOptionsService.getBookingOptionsFromAPI(ids.join(',')).then(
          async (response: IMenuOptionsResponse) => {
            if (response) {
              const payload: any = {};
              payload.implicitChildMenuOptions = response.data.splice(0, implicitIds.length);
              payload.explicitChildMenuOptions = response.data;
              await dispatch(updateActiveChildMenuOption(payload));
              resolve(null);
            }
          },
          (err: any) => {
            throw Error(err); // rethrow error so we can catch it below
          }
        );
      } catch (err) {
        console.error(NS, `"BookingUpsellOptionsService.getBookingOptionsFromAPI" failed`, err);
        dispatch(
          errorStateActions.setErrorState({
            errorTitle: 'Problem loading booking options',
            errorMessage: `There was a problem loading booking options. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
            accordionDetail: getAccordionDetail(err),
          })
        );
        resolve(null);
      }
    });
  }
);

export const updateSelectedMenuOption = createAsyncThunk(
  'BookingOptionsUpsell/updateSelectedMenuOption',
  async (
    payload: { selectedMenuOptions: IBookingMenuOption[] },
    { dispatch, getState }
  ): Promise<void> => {
    const { displayReducer } = getState() as DiaryRootState;
    const booking = displayReducer.activeBooking;
    const selectedUpsellMenuOption = !isEmpty(booking.selectedUpsellOptions)
      ? booking.selectedUpsellOptions.filter((s: IBookingMenuOption) => s.quantity > 0)
      : [];
    const newPayload = payload.selectedMenuOptions.filter((s) => s.quantity > 0);
    const newSelectedMenuOptions = [...selectedUpsellMenuOption, ...newPayload];
    // const newBooking = { ...booking, selectedMenuOptions: newSelectedMenuOptions };
    dispatch(displayActions.setSelectedMenuOptions(newSelectedMenuOptions));
  }
);

export const updateSelectedUpsellMenuOption = createAsyncThunk(
  'BookingOptionsUpsell/updateSelectedUpsellMenuOption',
  async (
    payload: { selectedMenuOptions: IBookingMenuOption[] },
    { dispatch, getState }
  ): Promise<void> => {
    const { displayReducer } = getState() as DiaryRootState;
    const booking = displayReducer.activeBooking;

    const selectedMenuOption = booking.selectedMenuOptions.filter(
      (s: IBookingMenuOption) => !s.isUpsellItem && s.quantity > 0
    );
    const newPayload = payload.selectedMenuOptions.filter((s) => s.quantity > 0);
    const newSelectedMenuOptions = [...selectedMenuOption, ...newPayload];

    // const newBooking = {
    //   ...booking,
    //   selectedMenuOptions: newSelectedMenuOptions,
    //   selectedUpsellOptions: newPayload,
    // };
    dispatch(displayActions.setSelectedMenuOptions(newSelectedMenuOptions));
    dispatch(displayActions.setSelectedUpsellOptions(newPayload));
  }
);

export const updateBookingUpsellOption = createAsyncThunk(
  'BookingOptionsUpsell/updateBookingUpsellOption',
  async (payload: { isUpsell?: boolean }, { dispatch, getState }): Promise<void> => {
    const { displayReducer } = getState() as DiaryRootState;
    const booking = displayReducer.activeBooking;
    await dispatch(
      updatePaymentBasedOnBookingOptions({
        selectedMenuOptions: booking.selectedMenuOptions,
        isUpsell: payload.isUpsell,
      })
    );
  }
);

export const updateCachedMenuOptionDetailsFromAPI = createAsyncThunk(
  'BookingOptionsUpsell/updateCachedMenuOptionDetailsFromAPI',
  async (
    payload: { ids: string[]; fullRefresh?: false },
    { dispatch, getState }
  ): Promise<void> => {
    BookingUpsellOptionsService.getBookingOptionsFromAPI(payload.ids.join(','))
      .then((response: IMenuOptionsResponse) => {
        if (response) {
          const value = { options: response.data, fullRefresh: payload.fullRefresh };
          dispatch(actions.updateCachedMenuOptionDetails(value));
        }
      })
      .catch((err: any) => {
        console.warn(
          NS,
          'updateCachedMenuOptionDetailsFromAPI - BookingUpsellOptionsService.getBookingOptionsFromAPI error',
          err
        );
        dispatch(
          errorStateActions.setErrorState({
            errorTitle: 'Booking upsell options error',
            errorMessage: `There was an error while getting booking options. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
            accordionDetail: getAccordionDetail(err),
          })
        );
      });
  }
);

export const updateBookingUpsellOptionExtras = createAsyncThunk(
  'BookingOptionsUpsell/updateBookingUpsellOptionExtras',
  async (
    payload: {
      BOData: IBookingMenuOptionExtrasUpdater;
      serviceId: string;
      bookingDate: Date;
      bookingTimes: IInfoAtTime[];
    },
    { dispatch, getState }
  ): Promise<void> => {
    return new Promise((resolve) => {
      const state = getState() as DiaryRootState;
      const booking = state.displayReducer.activeBooking;

      const { parentId, isSameForAll, explicitChildMenuOptions, implicitChildMenuOptions } =
        payload.BOData;
      /**
       * @param {[type]}
       */
      if (payload.BOData.parentQuantity > 0) {
        const parentBookingOption = booking.selectedMenuOptions.find(
          (val: IBookingMenuOption) => val.menuOptionId === parentId
        );
        const { quantity, menuOptionId } = parentBookingOption;
        const bookingOption: IBookingMenuOption = {
          quantity,
          menuOptionId,
          extras: {
            isSameForAll,
            explicitChildMenuOptions,
            implicitChildMenuOptions,
          },
        };
        const opts: IBookingMenuOption[] = [bookingOption];
        PaymentService.getPaymentTypesPerBookingOption(
          opts,
          payload.serviceId,
          payload.bookingDate,
          payload.bookingTimes,
          booking.people,
          true
        )
          .pipe(
            first(),
            map(({ data }) => data.paymentTypeName)
          ) //@todo: check why we are using paymentTypeName instead of paymentType
          .subscribe(
            (paymentType: servicePaymentType) => {
              const newPayload = {
                paymentTypeOverride: { paymentType, hasError: false },
                id: payload.BOData.parentId,
              };
              dispatch(updateBookingOptionPayment(newPayload));
              dispatch(updateMenuOptionExtras(payload.BOData));
              resolve(null);
            },
            (err: any) => {
              console.error(NS, `"PaymentService.getPaymentTypesPerBookingOption" failed`, err);
              const newPayload: any = {
                paymentTypeOverride: { paymentType: null, hasError: false },
                id: payload.BOData.parentId,
              };
              dispatch(
                errorStateActions.setErrorState({
                  errorTitle: 'Problem getting payment types',
                  errorMessage: `There was a problem getting payment types while updating booking options. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
                  accordionDetail: getAccordionDetail(err),
                })
              );
              dispatch(updateBookingOptionPayment(newPayload));
              dispatch(updateMenuOptionExtras(payload.BOData));
              resolve(null);
            }
          );
      }
    });
  }
);

export const updateMenuOptionExtras = createAsyncThunk(
  'BookingOptionsUpsell/updateMenuOptionExtras',
  async (payload: IBookingMenuOptionExtrasUpdater, { dispatch, getState }): Promise<void> => {
    const { displayReducer } = getState() as DiaryRootState;
    const booking = displayReducer.activeBooking;

    const { parentId, implicitChildMenuOptions, explicitChildMenuOptions, isSameForAll } = payload;

    const selectedMenuOptions = [...booking.selectedMenuOptions];

    const newSelectedMenuOption = selectedMenuOptions.map((options) => {
      if (options.menuOptionId === parentId) {
        const hasChildItems = implicitChildMenuOptions.length || explicitChildMenuOptions.length;
        if (hasChildItems) {
          const newExtras = {
            implicitChildMenuOptions,
            explicitChildMenuOptions,
            isSameForAll,
          };
          options = { ...options, extras: newExtras };
        } else {
          delete options.extras;
        }
      }
      return options;
    });

    // const newBooking = {
    //   ...booking,
    //   selectedMenuOptions: newSelectedMenuOption,
    // };
    dispatch(displayActions.setSelectedMenuOptions(newSelectedMenuOption));
  }
);

export const updateBookingOptionPayment = createAsyncThunk(
  'BookingOptionsUpsell/updateBookingOptionPayment',
  async (
    payload: IBookingOptionPayment,
    { dispatch, getState }
  ): Promise<{ temporaryPaymentDetailsForBO: IPaymentDetailsBase }> => {
    const { bookingOptionsUpsellReducer } = getState() as DiaryRootState;
    const temporaryPaymentDetailsForBO = bookingOptionsUpsellReducer.temporaryPaymentDetailsForBO;

    if (!temporaryPaymentDetailsForBO || !temporaryPaymentDetailsForBO.options) {
      return null;
    }

    const options: IServicePaymentOption[] = temporaryPaymentDetailsForBO.options.slice(); // creates a copy of the options array
    options.forEach((o) => {
      o = o.id === payload.id ? { ...o, paymentTypeOverride: payload.paymentTypeOverride } : null;
      return o;
    });

    const paymentDetails: IPaymentDetailsBase = {
      ...temporaryPaymentDetailsForBO,
      options,
    };

    return {
      temporaryPaymentDetailsForBO: paymentDetails,
    };
  }
);

export const updateActiveChildMenuOption = createAsyncThunk(
  'BookingOptionsUpsell/updateActiveChildMenuOption',
  async (
    payload: IUpdateActiveChildMenuOptionPayload,
    { dispatch, getState }
  ): Promise<{
    activeChildMenuOption: IActiveChildMenuOption;
    cachedMenuOptionDetails: ISimpleBookingOption[];
  }> => {
    const { bookingOptionsUpsellReducer } = getState() as DiaryRootState;

    const menuOptionsImplicit = payload ? payload.implicitChildMenuOptions : null;
    const menuOptionsExplicit = payload ? payload.explicitChildMenuOptions : null;

    const activeChildMenuOption: IActiveChildMenuOption = {
      ...bookingOptionsUpsellReducer.activeChildMenuOption,
      menuOptionsImplicit,
      menuOptionsExplicit,
    };

    // Here we update `state.cachedMenuOptionDetails` incase we need to request this menu option again (saves unnecessary backend calls)
    const cachedMenuOptionDetails = [...bookingOptionsUpsellReducer.cachedMenuOptionDetails];
    (menuOptionsImplicit || []).concat(menuOptionsExplicit || []).forEach((opt) => {
      const ind: number = cachedMenuOptionDetails.findIndex(({ id }) => id === opt.id);
      if (ind > -1) {
        cachedMenuOptionDetails[ind] = opt;
      } else {
        cachedMenuOptionDetails.push(opt);
      }
    });

    return {
      activeChildMenuOption,
      cachedMenuOptionDetails,
    };
  }
);

const bookingOptionsUpsellSlice = createSlice({
  name: 'BookingOptionsUpsell', // used in action types
  initialState,
  reducers: {
    clearCachedBookingOptions: (state: IBookingOptionsUpsellState, action) => {
      state.cachedMenuOptionDetails = [];
    },
    setEditCachedMenuOptionDetails: (
      state: IBookingOptionsUpsellState,
      action: PayloadAction<{ id: string }[]>
    ) => {
      state.editCachedMenuOptionDetails = action.payload;
    },
    updateCachedMenuOptionDetails: (
      state: IBookingOptionsUpsellState,
      action: PayloadAction<IUpdateCachedChildMenuOptionDetails>
    ) => {
      // Here we update `state.cachedMenuOptionDetails` incase we need to request this menu option again (saves unnecessary backend calls)

      const cachedMenuOptionDetails = action.payload.fullRefresh
        ? []
        : (state.cachedMenuOptionDetails as any);

      action.payload.options.forEach((opt) => {
        const ind: number = cachedMenuOptionDetails.findIndex(
          ({ id }: { id: any }) => id === opt.id
        );
        if (ind > -1) {
          cachedMenuOptionDetails[ind] = opt;
        } else {
          cachedMenuOptionDetails.push(opt);
        }
      });

      return {
        ...state,
        cachedMenuOptionDetails,
      };
    },
    setIsBookingOption: (state: IBookingOptionsUpsellState, action: PayloadAction<boolean>) => {
      state.isBookingOption = action.payload;
    },
    setBookingOptionValidity: (
      state: IBookingOptionsUpsellState,
      action: PayloadAction<boolean>
    ) => {
      state.bookingOptionsValid = action.payload;
    },
    setIsUpsellOption: (state: IBookingOptionsUpsellState, action: PayloadAction<boolean>) => {
      state.isUpsellOptions = action.payload;
    },
    setEnableBookingUpsellOptions: (
      state: IBookingOptionsUpsellState,
      action: PayloadAction<boolean>
    ) => {
      state.enableBookingUpsellOptions = action.payload;
    },
    setEnableBookingOptions: (
      state: IBookingOptionsUpsellState,
      action: PayloadAction<boolean>
    ) => {
      state.enableBookingOptions = action.payload;
    },
    setUpsellOptionValidity: (
      state: IBookingOptionsUpsellState,
      action: PayloadAction<boolean>
    ) => {
      state.upsellOptionsValid = action.payload;
    },
    setBookingOptionsToggle: (
      state: IBookingOptionsUpsellState,
      action: PayloadAction<boolean>
    ) => {
      state.showBookingOptions = action.payload;
    },
    setUpsellOptionsToggle: (state: IBookingOptionsUpsellState, action: PayloadAction<boolean>) => {
      state.showUpsellOptions = action.payload;
    },
    setIsPaxThresholdValidForEdit: (
      state: IBookingOptionsUpsellState,
      action: PayloadAction<boolean>
    ) => {
      state.isPaxThresholdValidForEdit = action.payload;
    },
    setBookingUpsellOptionDate: (
      state: IBookingOptionsUpsellState,
      action: PayloadAction<Date>
    ) => {
      state.bookingUpsellOptionDate = action.payload;
    },
    setAllBookingOptionsFromAPI: (
      state: IBookingOptionsUpsellState,
      action: PayloadAction<IPouchServicePaymentOption[]>
    ) => {
      state.allBookingOptions = action.payload;
    },
    reset: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      settingUpDetailsForBookingUpsellOptions.fulfilled,
      (state: IBookingOptionsUpsellState, action) => {
        state.editCachedMenuOptionDetails = action.payload?.editCachedMenuOptionDetails;
        state.bookingOptionsValid = action.payload?.bookingOptionsValid;
        state.isBookingOption = action.payload?.isBookingOption;
        state.isUpsellOptions = action.payload?.isUpsellOptions;
        state.upsellOptionsValid = action.payload?.upsellOptionsValid;
        state.temporaryPaymentDetailsForBO = action.payload?.newPaymentDetails;
        if (action.payload) {
          state.isPaxThresholdValidForEdit = action.payload.isPaxThresholdValidForEdit;
        }
      }
    );
    builder.addCase(
      updateActiveChildMenuOption.fulfilled,
      (state: IBookingOptionsUpsellState, action) => {
        state.activeChildMenuOption = action.payload?.activeChildMenuOption;
        state.cachedMenuOptionDetails = action.payload?.cachedMenuOptionDetails;
      }
    );
    builder.addCase(
      updateBookingOptionPayment.fulfilled,
      (state: IBookingOptionsUpsellState, action) => {
        state.temporaryPaymentDetailsForBO = action.payload?.temporaryPaymentDetailsForBO;
      }
    );
  },
});

function updateMenuOptionCachedDetails(
  allBookingOptions: IPouchServicePaymentOption[]
): { id: string }[] {
  return allBookingOptions.map((option) => ({ ...option, id: option._id }));
}

// Extract the action creators object and the reducer
const { actions, reducer } = bookingOptionsUpsellSlice;
// Extract and export each action creator by name
export { actions as bookingOptionsUpsellActions };
// Export the reducer, either as a default or named export
export default reducer;
